import React from 'react';
import { useTranslation } from 'react-i18next';

import { RESULT_DESIGN_PLACEHOLDER } from '@/constants/resultDesignPlaceholder';
import type { EvalSummaryStats } from './simulationResultBlocks';

export interface EvalStatCardProps {
  label: string;
  value: string | number;
  sub?: string;
  /** Renderer 정답율 카드 그리드(ItemRateCards)용 작은 카드 */
  variant?: 'default' | 'compact';
}

export default function EvalStatCard({ label, value, sub, variant = 'default' }: EvalStatCardProps): React.ReactElement {
  if (variant === 'compact') {
    return (
      <div className="bg-white rounded-lg border border-slate-200/90 px-3 py-2 shadow-sm">
        <span className="text-xs text-gray-500 block">{label}</span>
        <div className="flex items-baseline gap-1 mt-0.5">
          <span className="text-base font-semibold text-gray-900">{value}</span>
          {sub != null && sub !== '' && <span className="text-sm text-gray-500">{sub}</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 md:p-5 rounded-xl border border-slate-200/90 shadow-sm">
      <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
      <div className="flex items-baseline gap-1 mt-1">
        <span className="text-xl font-bold text-gray-900">{value}</span>
        {sub != null && sub !== '' && <span className="text-sm text-gray-500">{sub}</span>}
      </div>
    </div>
  );
}

function isEvalSummaryEmpty(v: unknown): boolean {
  return v == null || v === '';
}

/** 정답율 / 정답수·전체 / 소요시간 — 동일 EvalStatCard(default) 포맷 3열 */
export function EvalSummaryStatGrid({
  stats,
  className = '',
  isAllParticipantsView = false,
}: {
  stats: EvalSummaryStats;
  className?: string;
  isAllParticipantsView?: boolean;
}): React.ReactElement {
  const { t } = useTranslation('scenario');
  const empty = RESULT_DESIGN_PLACEHOLDER;
  const scoreVal: string | number = isEvalSummaryEmpty(stats.score)
    ? empty
    : (typeof stats.score === 'number' ? stats.score : String(stats.score));
  const scoreSub = isEvalSummaryEmpty(stats.scoreSub) ? undefined : stats.scoreSub ?? undefined;

  // 전체 참가자 화면: 환자 수만 표시, 개인 결과: 정답수/전체 표시
  const hasPair = !isEvalSummaryEmpty(stats.correctCount) && !isEvalSummaryEmpty(stats.total);
  const correctVal = isAllParticipantsView 
    ? (isEvalSummaryEmpty(stats.total) ? empty : String(stats.total))
    : (hasPair ? String(stats.correctCount) : empty);
  const correctSub = isAllParticipantsView ? undefined : (hasPair ? `/ ${stats.total}` : undefined);
  const correctLabel = isAllParticipantsView ? '환자 수' : t('evalSummary.correctOfTotal');

  const durationVal = isEvalSummaryEmpty(stats.duration) ? empty : String(stats.duration);

  return (
    <div className={`grid grid-cols-2 md:grid-cols-3 gap-4 ${className}`.trim()}>
      <EvalStatCard label={t('evalSummary.accuracyRate')} value={scoreVal} sub={scoreSub} />
      <EvalStatCard label={correctLabel} value={correctVal} sub={correctSub} />
      <EvalStatCard label={t('evalSummary.duration')} value={durationVal} />
    </div>
  );
}
